import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Rocket,
  Target,
  Zap,
  Globe,
  MapPin,
  Building,
  Infinity,
  Presentation,
  ChevronDown,
  ChevronUp,
  Armchair
} from 'lucide-react';
import { useOutletContext } from "react-router-dom";

export default function RetrofuturismRoadmap() {
  const { theme } = useOutletContext();
  const isDark = theme === "dark";
  const [expandedIndex, setExpandedIndex] = useState(null);

  const timelineData = [
    {
      number: "01",
      year: "2016",
      title: "Launch",
      content: [
        "Established flagship centre at Whitefield Bangalore & Anna Nagar Chennai",
      ],
      opposite: "2 Centres (1,300 seats)",
      icon: <Rocket className="w-4 h-4" />,
      color: "text-blue-600"
    },
    {
      number: "02",
      year: "2019",
      title: "Expansion",
      content: [
        "Expanded into three new micro-markets in Chennai, Pune, and Hyderabad",
      ],
      opposite: "3 Centres (4,000 seats)",
      icon: <Globe className="w-4 h-4" />,
      color: "text-violet-600"
    },
    {
      number: "03",
      year: "2023",
      title: "New Segment",
      content: [
        "Ventured into the Managed Office segment in Pune with 800 seats",
      ],
      opposite: "1 Centre (800 seats)",
      icon: <Building className="w-4 h-4" />,
      color: "text-emerald-600"
    },
    {
      number: "04",
      year: "2024",
      title: "Managed Office Growth",
      content: [
        "Expanded the Managed Office division with two new deals in Pune",
        "Resilient through COVID"
      ],
      opposite: "1 Centre (350 seats)",
      icon: <Target className="w-4 h-4" />,
      color: "text-amber-600"
    },
    {
      number: "05",
      year: "2025",
      title: "Expansion",
      content: [
        "Opened 3 new coworking centres - PTP, SKCL & Keppel",
        "Expanded managed office presence in Noida with 2 centres",
      ],
      opposite: "5 Centres (2,500 seats)",
      icon: <Zap className="w-4 h-4" />,
      color: "text-pink-600"
    },
    {
      number: "06",
      year: "2026 & onwards",
      title: "Growth continues",
      content: [
        "Signed 4 deals, expanding our presence in Bangalore, Chennai and Noida",
      ],
      opposite: "3 Centres (2,200 seats)",
      icon: <Infinity className="w-4 h-4" />,
      color: "text-cyan-600"
    }
  ];

  return (
    <div
      className={`py-6 ${isDark ? "bg-black text-white" : "bg-white text-gray-900"
        }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-16 md:mb-20"
        >
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2">
              <div className={`w-4 h-px ${isDark ? "bg-slate-600" : "bg-gray-300"}`}></div>
              <span
                className={`text-xs font-medium  uppercase ${isDark ? "text-slate-300" : "text-gray-500"
                  }`}
              >
                Our Journey
              </span>
              <div className={`w-4 h-px ${isDark ? "bg-slate-600" : "bg-gray-300"}`}></div>
            </div>
          </div>

          {/* Main Title */}
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-light mb-4 liber">
              Timeline of{" "}
              <span className={`font-normal ${isDark ? "text-white" : ""}`}>Innovation</span>
            </h1>
            <p
              className={`max-w-2xl mx-auto text-base leading-relaxed ${isDark ? "text-slate-300" : "text-gray-600"
                }`}
            >
              Explore The Hive's evolution from innovative concept to a leading multi-city workspace network.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {[
              { value: "7+", label: "Cities", icon: MapPin },
              { value: "15+", label: "Centres", icon: Building },
              { value: "11,000+", label: "Seats", icon: Armchair },
              { value: "0.7 Million", label: "Sq.ft Area", icon: Presentation }
            ].map((stat, index) => {
              const Icon = stat.icon;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  className="text-center"
                >
                  <div className={`text-2xl font-light mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
                    {stat.value}
                  </div>
                  <div className={`text-xs flex items-center justify-center ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                    <Icon
                      className={`w-5 mr-1 h-5 ${isDark ? "text-white" : "text-black"
                        }`}
                    /> {stat.label}
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div
            className={`absolute left-4 md:left-1/2 md:transform md:-translate-x-1/2 top-0 bottom-0 w-px ${isDark ? "bg-slate-800" : "bg-gray-200"
              }`}
          ></div>

          {/* Timeline Items */}
          <div className="space-y-6">
            {timelineData.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                {/* Timeline Dot */}
                <div
                  className={`absolute left-[10px]  md:left-1/2 md:transform md:-translate-x-1/2 w-3 h-3 rounded-full z-10 border ${isDark ? "bg-black border-slate-700" : "bg-white border-gray-300"
                    }`}
                ></div>

                {/* Content */}
                <div className={`pl-12  md:pl-0 md:flex md:items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Left/Right Content */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'} mb-8 md:mb-0`}>
                    {/* Year */}
                    <div className="mb-2">
                      <span
                        className={`text-xs font-medium tracking-wider ${isDark ? "text-slate-400" : "text-gray-500"
                          }`}
                      >
                        {item.year}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className={`text-xl font-normal mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      {item.title}
                    </h3>

                    {/* Content */}
                    <div className={`space-y-3 mb-6`}>
                      {item.content.map((text, i) => {
                        const isExpanded = expandedIndex === index;
                        const shouldShow = isExpanded || i < 1;
                        if (!shouldShow) return null;
                        
                        return (
                          <p
                          key={i}
                          className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-gray-600"}`}
                        >
                          {text}
                        </p>
                        );
                      })}
                      
                      {item.content.length > 1 && (
                        <button
                          onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                          className={`inline-flex items-center gap-1 text-xs font-medium mt-2 ${isDark ? "text-orange-400 hover:text-orange-300" : "text-orange-600 hover:text-orange-700"}`}
                        >
                          {expandedIndex === index ? (
                            <>Read less <ChevronUp className="w-3 h-3" /></>
                          ) : (
                            <>Read more <ChevronDown className="w-3 h-3" /></>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Icon - Mobile only */}
                    <div className="inline-flex md:hidden items-center gap-2">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center border ${item.color} border-current ${isDark ? "bg-white/5" : ""
                          }`}
                      >
                        {React.cloneElement(item.icon, { className: "w-3 h-3" })}
                      </div>
                      <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        {item.opposite}
                      </span>
                    </div>
                  </div>

                  {/* Opposite Side Content (Icon) for Desktop */}
                  <div className={`hidden md:flex md:w-1/2 ${index % 2 === 0 ? 'md:pl-12 justify-start' : 'md:pr-12 justify-end'}`}>
                    <div className="inline-flex items-center gap-2">
                      {index % 2 !== 0 && (
                        <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                          {item.opposite}
                        </span>
                      )}
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center border ${item.color} border-current ${isDark ? "bg-white/5" : ""
                          }`}
                      >
                        {React.cloneElement(item.icon, { className: "w-3 h-3" })}
                      </div>
                      {index % 2 === 0 && (
                        <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                          {item.opposite}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Number */}
                {/* <div className="absolute top-0 left-0 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center ${isDark ? "bg-white/10" : "bg-gray-100"
                      }`}
                  >
                    <span className={`text-xs font-medium ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                      {item.number}
                    </span>
                  </div>
                </div> */}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Future Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10"
        >
          <div className="max-w-2xl mx-auto">
            {/* Divider */}
            <div className="flex items-center justify-center gap-4 mb-2">
              <div className={`flex-1 h-px ${isDark ? "bg-slate-700" : "bg-gray-200"}`}></div>
              <Infinity className={`w-5 h-5 ${isDark ? "text-slate-400" : "text-gray-400"}`} />
              <div className={`flex-1 h-px ${isDark ? "bg-slate-700" : "bg-gray-200"}`}></div>
            </div>


          </div>
        </motion.div>


      </div>
    </div>
  );
}